/**
 * Shared bin configuration for time-based grouping of health data.
 *
 * 4 bins (single source of truth):
 *   latest:  0–15 days
 *   prior:   15–90 days
 *   past:    90 days–1 year
 *   history: >1 year
 *
 * Used by chat-agent (tools.mjs) via deploy-time copy.
 * Python equivalent: shared-utils/bin_config.py (delivered via Lambda Layer).
 *
 * SYNC WARNING: This file must stay in sync with bin_config.py (Python port).
 * Both files contain identical logic — BIN_BOUNDARIES, BIN_ORDER, and all functions.
 * When modifying this file, update bin_config.py to match.
 */

export const BIN_BOUNDARIES = {
  latest: 15,    // days
  prior: 90,
  past: 365,
  // history: everything beyond 365
};

export const BIN_ORDER = ['history', 'past', 'prior', 'latest'];

/**
 * Assign a time bin to a date based on days elapsed from reference.
 * @param {string|number|Date} testDate - The date to bin (ISO string, YYYYMMDD number, or Date)
 * @param {Date} referenceDate - Current date for comparison
 * @returns {'latest'|'prior'|'past'|'history'}
 */
export function getTimeBin(testDate, referenceDate) {
  const testMs = parseDate(testDate)?.getTime();
  if (!testMs || isNaN(testMs)) return 'history';
  const diffDays = (referenceDate.getTime() - testMs) / (1000 * 60 * 60 * 24);
  if (diffDays <= BIN_BOUNDARIES.latest) return 'latest';
  if (diffDays <= BIN_BOUNDARIES.prior) return 'prior';
  if (diffDays <= BIN_BOUNDARIES.past) return 'past';
  return 'history';
}

/**
 * Compute the actual calendar date ranges for each bin.
 * @param {Date} referenceDate
 * @returns {Object} { latest: { from, to }, prior: { from, to }, past: { from, to }, history: { from, to } }
 */
export function getBinDateRanges(referenceDate) {
  const ref = referenceDate.getTime();
  const day = 1000 * 60 * 60 * 24;

  const latestFrom = new Date(ref - BIN_BOUNDARIES.latest * day);
  const priorFrom = new Date(ref - BIN_BOUNDARIES.prior * day);
  const pastFrom = new Date(ref - BIN_BOUNDARIES.past * day);

  return {
    latest: { from: latestFrom, to: referenceDate },
    prior: { from: priorFrom, to: latestFrom },
    past: { from: pastFrom, to: priorFrom },
    history: { from: null, to: pastFrom },
  };
}

/**
 * Format a bin's date range as a human-readable label.
 * @param {string} binName - 'latest'|'prior'|'past'|'history'
 * @param {Date} referenceDate
 * @returns {string} e.g. "May 10 – May 25, 2026" or "Before May 2025"
 */
export function formatBinLabel(binName, referenceDate) {
  const ranges = getBinDateRanges(referenceDate);
  const range = ranges[binName];
  if (!range) return binName;

  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const fmtShort = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  if (binName === 'history') {
    return `Before ${fmtShort(range.to)}`;
  }

  return `${fmt(range.from)} – ${fmt(range.to)}`;
}

/**
 * Determine direction of change between two numeric values.
 */
export function getDirection(older, newer) {
  if (older === newer) return 'stable';
  return newer > older ? 'increasing' : 'decreasing';
}

/**
 * Check if a value crosses a reference range boundary between two tests.
 */
export function checkBoundaryCrossing(olderValue, newerValue, refLow, refHigh) {
  const crossings = [];
  if (olderValue <= refHigh && newerValue > refHigh) crossings.push('crossed_above_high');
  if (olderValue >= refLow && newerValue < refLow) crossings.push('crossed_below_low');
  if (olderValue > refHigh && newerValue <= refHigh) crossings.push('returned_below_high');
  if (olderValue < refLow && newerValue >= refLow) crossings.push('returned_above_low');
  return crossings;
}

/**
 * Parse a date from various formats (YYYYMMDD number, ISO string, Date).
 * @param {string|number|Date} input
 * @returns {Date|null}
 */
export function parseDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input) ? null : input;

  const str = String(input);
  // YYYYMMDD numeric
  if (/^\d{8}$/.test(str)) {
    return new Date(`${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6, 8)}`);
  }
  const parsed = new Date(str);
  return isNaN(parsed) ? null : parsed;
}

/**
 * Format a Date as YYYYMMDD string.
 */
export function formatDateYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

/**
 * Format a Date as YYYY-MM-DD string.
 */
export function formatDateISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
